import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Search,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RotateCw,
  Copy,
  FileDown,
  FileText,
  Zap,
  Info,
  AlertTriangle,
  Check,
  Undo2,
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

/**
 * GSC Indexing admin tab — rebuilt May 30, 2026.
 *
 * Why the rewrite:
 * Google's Indexing API officially only supports JobPosting +
 * BroadcastEvent content. For a blog/SaaS app, every URL was 403'ing
 * (regardless of who's Owner of the property, regardless of the API
 * being enabled). The old tile had a textarea + "Submit to Google"
 * button that quietly never worked.
 *
 * What replaces it:
 * A curated, prioritized list of URLs from /api/admin/gsc-indexing/
 * priority-urls, each row paired with a "Open in GSC Inspection" deep
 * link. The admin clicks → lands on Google's URL Inspection page
 * pre-filled → clicks Request Indexing in Google's UI. Most reliable
 * indexing path that exists, works for any content type, no
 * permissions hell.
 *
 * The auto-drainer + sitemap submit + manual API ping remain available
 * (collapsed in an "Advanced" details block) for the rare case the API
 * actually works for some content.
 */

const GscIndexingTab = () => {
  const [priority, setPriority] = useState(null);
  const [priorityLoading, setPriorityLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('');
  const [hideSubmitted, setHideSubmitted] = useState(false);
  const [busyUrls, setBusyUrls] = useState({}); // url → bool (mark/unmark in-flight)

  const [urls, setUrls] = useState('https://clearcommunicationapp.com/');
  const [type, setType] = useState('URL_UPDATED');
  const [busy, setBusy] = useState(false);
  const [sitemapBusy, setSitemapBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [drainer, setDrainer] = useState(null);
  const [drainerBusy, setDrainerBusy] = useState(false);
  const [drainerRunBusy, setDrainerRunBusy] = useState(false);

  // --- Relative-time formatter for submitted_at pills ---
  const formatRelative = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diffMs = Date.now() - then;
    const s = Math.floor(diffMs / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  const formatAbsolute = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const loadAll = async () => {
    setPriorityLoading(true);
    const [p, s, h, d] = await Promise.all([
      apiFetch('/api/admin/gsc-indexing/priority-urls'),
      apiFetch('/api/admin/gsc-indexing/status'),
      apiFetch('/api/admin/gsc-indexing/history?limit=10'),
      apiFetch('/api/admin/gsc-indexing/drainer/config'),
    ]);
    setPriorityLoading(false);
    if (!p.error) setPriority(p.data);
    if (!s.error) setStatus(s.data);
    if (!h.error) setHistory(h.data?.runs || []);
    if (!d.error) setDrainer(d.data);
  };

  useEffect(() => {
    loadAll();
  }, []);

  // --- Helpers ---
  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Clipboard blocked — long-press to copy.');
    }
  };

  const copyAllUrls = async (group) => {
    const list = (priority?.[group] || []).map((it) => it.url).join('\n');
    if (!list) return;
    try {
      await navigator.clipboard.writeText(list);
      toast.success(`${priority[group].length} URLs copied`);
    } catch {
      toast.error('Clipboard blocked.');
    }
  };

  // --- Filter (across both tiers) ---
  const matches = (it, q) => {
    if (!q) return true;
    return (
      it.url.toLowerCase().includes(q) ||
      (it.label || '').toLowerCase().includes(q)
    );
  };

  const filteredCore = useMemo(() => {
    if (!priority) return [];
    const q = filter.trim().toLowerCase();
    return priority.core.filter(
      (it) => matches(it, q) && !(hideSubmitted && it.submitted_at),
    );
  }, [priority, filter, hideSubmitted]);

  const filteredBlog = useMemo(() => {
    if (!priority) return [];
    const q = filter.trim().toLowerCase();
    return priority.blog.filter(
      (it) => matches(it, q) && !(hideSubmitted && it.submitted_at),
    );
  }, [priority, filter, hideSubmitted]);

  const submittedTotal = useMemo(() => {
    if (!priority) return 0;
    return (
      priority.core.filter((it) => it.submitted_at).length +
      priority.blog.filter((it) => it.submitted_at).length
    );
  }, [priority]);

  // --- Mark / unmark submitted ---
  const patchItem = (url, patch) => {
    setPriority((prev) => {
      if (!prev) return prev;
      const apply = (arr) =>
        arr.map((it) => (it.url === url ? { ...it, ...patch } : it));
      return { ...prev, core: apply(prev.core), blog: apply(prev.blog) };
    });
  };

  const markSubmitted = async (url) => {
    setBusyUrls((b) => ({ ...b, [url]: true }));
    try {
      const { data, error } = await apiFetch('/api/admin/gsc-indexing/mark-submitted', {
        method: 'POST',
        body: { url },
      });
      if (error) return toast.error(error || 'Could not mark submitted.');
      patchItem(url, {
        submitted_at: data?.submitted_at,
        submitted_by_email: priority?.current_admin_email,
      });
      toast.success('Marked submitted.');
    } finally {
      setBusyUrls((b) => {
        const { [url]: _gone, ...rest } = b;
        return rest;
      });
    }
  };

  const unmarkSubmitted = async (url) => {
    setBusyUrls((b) => ({ ...b, [url]: true }));
    try {
      const { error } = await apiFetch(
        `/api/admin/gsc-indexing/mark-submitted?url=${encodeURIComponent(url)}`,
        { method: 'DELETE' },
      );
      if (error) return toast.error(error || 'Could not unmark.');
      patchItem(url, { submitted_at: null, submitted_by_email: null });
      toast.success('Unmarked.');
    } finally {
      setBusyUrls((b) => {
        const { [url]: _gone, ...rest } = b;
        return rest;
      });
    }
  };

  // --- Drainer (auto-sitemap-drain) actions ---
  const toggleDrainer = async (next) => {
    setDrainerBusy(true);
    try {
      const { error } = await apiFetch('/api/admin/gsc-indexing/drainer/config', {
        method: 'PUT',
        body: { enabled: next },
      });
      if (error) return toast.error(error || 'Could not update drainer config.');
      toast.success(next ? 'Daily sitemap drainer ON.' : 'Drainer OFF.');
      loadAll();
    } finally {
      setDrainerBusy(false);
    }
  };

  const runDrainerNow = async () => {
    setDrainerRunBusy(true);
    try {
      const { data, error } = await apiFetch('/api/admin/gsc-indexing/drainer/run-now', {
        method: 'POST',
        body: {},
      });
      if (error) return toast.error(error || 'Drain failed.');
      if (data?.skipped) toast.info(`Drain skipped: ${data.skipped}`);
      else
        toast.success(
          `Drained ${data.succeeded}/${data.submitted} URLs (${data.remaining_in_sitemap} remaining).`,
        );
      loadAll();
    } finally {
      setDrainerRunBusy(false);
    }
  };

  // --- Manual API ping (legacy, hidden behind "Advanced") ---
  const submitPing = async () => {
    const list = urls.split('\n').map((u) => u.trim()).filter(Boolean);
    if (list.length === 0) return toast.error('Paste at least one URL.');
    if (list.length > 50) return toast.error('Max 50 URLs per submission.');
    setBusy(true);
    setLastResult(null);
    try {
      const { data, error, status: code } = await apiFetch('/api/admin/gsc-indexing/ping', {
        method: 'POST',
        body: { urls: list, type },
      });
      if (error) return toast.error(error || `Ping failed (${code})`);
      setLastResult(data);
      if (data.succeeded === data.submitted)
        toast.success(`All ${data.succeeded} URLs submitted ✓`);
      else
        toast.warning(`${data.succeeded}/${data.submitted} succeeded. See detail below.`);
      loadAll();
    } finally {
      setBusy(false);
    }
  };

  const submitSitemap = async () => {
    setSitemapBusy(true);
    setLastResult(null);
    try {
      const { data, error, status: code } = await apiFetch('/api/admin/gsc-indexing/ping-sitemap', {
        method: 'POST',
        body: {},
      });
      if (error) return toast.error(error || `Sitemap ping failed (${code})`);
      setLastResult(data);
      const note =
        data.sitemap_total_seen > data.submitted
          ? ` (capped at 50 of ${data.sitemap_total_seen})`
          : '';
      if (data.succeeded === data.submitted)
        toast.success(`Submitted ${data.succeeded} sitemap URLs ✓${note}`);
      else if (data.hint) toast.error(data.hint, { duration: 12000 });
      else toast.warning(`${data.succeeded}/${data.submitted} succeeded${note}.`);
      loadAll();
    } finally {
      setSitemapBusy(false);
    }
  };

  const downloadPlayIntegrityDoc = async () => {
    try {
      const token = localStorage.getItem('cca_jwt') || '';
      const res = await fetch('/api/admin/docs/app-store-and-play-integrity', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return toast.error(`Download failed (${res.status}).`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AppStore_and_PlayIntegrity_Walkthrough.md';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded.');
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`);
    }
  };

  // --- Render row for a single URL ---
  const Row = ({ item }) => {
    const submitted = !!item.submitted_at;
    const busy = !!busyUrls[item.url];
    return (
      <li
        key={item.url}
        className={`flex items-start sm:items-center gap-2 py-2 px-2 hover:bg-muted/30 rounded-md flex-col sm:flex-row ${
          submitted ? 'opacity-75' : ''
        }`}
        data-testid={`gsc-url-row-${item.url}`}
      >
        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium truncate">{item.label}</p>
            {submitted && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded-full"
                title={`Submitted ${formatAbsolute(item.submitted_at)}${
                  item.submitted_by_email ? ` by ${item.submitted_by_email}` : ''
                }`}
                data-testid={`gsc-submitted-pill-${item.url}`}
              >
                <Check className="h-2.5 w-2.5" /> submitted {formatRelative(item.submitted_at)}
              </span>
            )}
          </div>
          <p className="text-xs font-mono text-muted-foreground truncate">{item.url}</p>
        </div>
        <div className="flex gap-1.5 self-end sm:self-auto shrink-0 flex-wrap">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={() => copyUrl(item.url)}
            title="Copy URL"
            data-testid={`gsc-copy-url-${item.url}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-[#00A896]/40 hover:bg-[#00E5CC]/10"
            asChild
            data-testid={`gsc-inspect-${item.url}`}
          >
            <a href={item.inspect_url} target="_blank" rel="noopener noreferrer">
              Open in Inspection <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </Button>
          {submitted ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              disabled={busy}
              onClick={() => unmarkSubmitted(item.url)}
              title="Undo — mark not yet submitted"
              data-testid={`gsc-unmark-${item.url}`}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Undo2 className="h-3 w-3 mr-1" /> Undo
                </>
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              disabled={busy}
              onClick={() => markSubmitted(item.url)}
              title="Mark this URL as submitted to GSC"
              data-testid={`gsc-mark-${item.url}`}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Check className="h-3 w-3 mr-1" /> Mark submitted
                </>
              )}
            </Button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4" data-testid="gsc-indexing-tab">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-[#00A896]" />
            Google Search Console — Request Indexing
          </CardTitle>
          <CardDescription>
            One-click GSC Inspection deep-links for your priority URLs. Open each link → press
            "Request Indexing" in Google's UI → Googlebot recrawls within hours.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Why-not-API banner */}
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm space-y-1.5"
            data-testid="gsc-api-policy-banner"
          >
            <p className="flex items-start gap-2 font-medium">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              Why we don't use the Indexing API for blog content
            </p>
            <p className="text-xs text-muted-foreground pl-6">
              Google's Indexing API officially only supports{' '}
              <code className="text-foreground">JobPosting</code> and{' '}
              <code className="text-foreground">BroadcastEvent</code> pages. Blog and marketing
              URLs get rejected with 403s regardless of permissions. Manual GSC URL Inspection
              (below) is the reliable path — and it takes about 20 seconds per URL.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Priority URLs — the main attraction */}
      <Card data-testid="priority-urls-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Priority URLs to submit</CardTitle>
              <CardDescription>
                Core pages always at the top, then the latest 30 blog posts.
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={loadAll}
              className="h-8"
              data-testid="refresh-priority-btn"
            >
              <RotateCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter + hide-submitted toggle */}
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              placeholder="Filter URLs or titles…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-sm"
              data-testid="gsc-priority-filter"
            />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideSubmitted}
                onChange={(e) => setHideSubmitted(e.target.checked)}
                className="h-3.5 w-3.5 accent-[#00A896]"
                data-testid="gsc-hide-submitted-toggle"
              />
              Hide already-submitted ({submittedTotal})
            </label>
          </div>

          {/* Loading state */}
          {priorityLoading && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Core URLs group */}
          {priority && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  Core pages
                  <Badge variant="secondary" className="text-[10px]">
                    {filteredCore.length}
                  </Badge>
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => copyAllUrls('core')}
                  data-testid="gsc-copy-all-core"
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy all
                </Button>
              </div>
              {filteredCore.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No matches.</p>
              ) : (
                <ul className="divide-y rounded-md border bg-card">
                  {filteredCore.map((it) => (
                    <Row key={it.url} item={it} />
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Blog posts group */}
          {priority && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  Recent blog posts
                  <Badge variant="secondary" className="text-[10px]">
                    {filteredBlog.length}
                  </Badge>
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => copyAllUrls('blog')}
                  data-testid="gsc-copy-all-blog"
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy all
                </Button>
              </div>
              {filteredBlog.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No matches.</p>
              ) : (
                <ul className="divide-y rounded-md border bg-card">
                  {filteredBlog.map((it) => (
                    <Row key={it.url} item={it} />
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Help text */}
          {priority && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer font-medium text-[#00A896]">
                How does this work?
              </summary>
              <ol className="list-decimal pl-5 mt-2 space-y-1">
                <li>Click <strong>"Open in Inspection"</strong> on any URL above.</li>
                <li>Google Search Console loads the URL Inspection result in a new tab.</li>
                <li>
                  Press the blue <strong>"Request Indexing"</strong> button in Google's UI.
                  Googlebot re-crawls the URL within hours (vs weeks for organic discovery).
                </li>
                <li>
                  Google rate-limits to ~10 indexing requests per day per property. Prioritise
                  newly-published blog posts and pages with recent canonical/SEO changes.
                </li>
              </ol>
            </details>
          )}
        </CardContent>
      </Card>

      {/* Daily sitemap drainer */}
      {drainer && (
        <Card data-testid="drainer-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#00A896]" /> Auto-drain sitemap (background job)
            </CardTitle>
            <CardDescription className="text-xs">
              When ON, scheduler submits {drainer.batch_size} URL(s) every day at 4:30 AM UTC,
              re-pinging each URL once every {drainer.min_age_days} days. Best-effort — most
              calls will 403 due to the policy above, but it's free to keep running for the rare
              URLs that do succeed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-[11px] text-muted-foreground mb-2">
              Drain state: <strong>{drainer.drain_state_url_count}</strong> URLs tracked
              {drainer.last_run && (
                <>
                  {' '}· Last run: {new Date(drainer.last_run.ran_at).toLocaleString()} (
                  {drainer.last_run.success_count}/{drainer.last_run.url_count} ok)
                </>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={drainer.enabled ? 'default' : 'outline'}
                onClick={() => toggleDrainer(!drainer.enabled)}
                disabled={drainerBusy}
                className={
                  drainer.enabled
                    ? 'bg-[#00A896] hover:bg-[#00876E] text-white h-7'
                    : 'h-7'
                }
                data-testid="drainer-toggle-btn"
              >
                {drainerBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                {drainer.enabled ? 'Daily drainer ON' : 'Turn ON daily drainer'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={runDrainerNow}
                disabled={drainerRunBusy || !drainer.enabled}
                className="h-7 text-xs"
                data-testid="drainer-run-now-btn"
              >
                {drainerRunBusy ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Draining…
                  </>
                ) : (
                  <>Run drain now</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Companion download — Play Integrity walkthrough */}
      <Card data-testid="play-integrity-doc-card">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <FileDown className="h-5 w-5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1.5">
              <p className="text-sm font-medium">App Store + Play Integrity walkthrough</p>
              <p className="text-xs text-muted-foreground">
                Step-by-step guide to fix Apple App Review rejections and wire up Google's Play
                Integrity API. Markdown — open in any text editor.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-purple-500/40 hover:bg-purple-500/10"
                onClick={downloadPlayIntegrityDoc}
                data-testid="download-play-integrity-doc-btn"
              >
                <FileDown className="h-3 w-3 mr-1" /> Download walkthrough
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced — legacy API ping (collapsed) */}
      <details className="rounded-lg border bg-card" data-testid="advanced-api-details">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <AlertTriangle className="h-4 w-4" /> Advanced — Direct Indexing API (legacy, mostly
          fails)
        </summary>
        <div className="p-4 pt-2 space-y-4 border-t">
          {status && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
              <div className="flex items-center gap-2 text-xs">
                {status.service_account_loaded ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Service account JSON
                    loaded
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Service account JSON
                    missing
                  </>
                )}
              </div>
              {status.service_account_email && (
                <div className="text-[10px] font-mono break-all text-muted-foreground">
                  {status.service_account_email}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground">
                Today: {status.daily_runs} / {status.daily_quota_hint} daily quota used
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex gap-2 text-sm">
              <button
                type="button"
                onClick={() => setType('URL_UPDATED')}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  type === 'URL_UPDATED'
                    ? 'bg-[#00A896] text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
                data-testid="type-updated-btn"
              >
                URL_UPDATED
              </button>
              <button
                type="button"
                onClick={() => setType('URL_DELETED')}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  type === 'URL_DELETED'
                    ? 'bg-[#8B5CF6] text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
                data-testid="type-deleted-btn"
              >
                URL_DELETED
              </button>
            </div>
            <Textarea
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              rows={5}
              className="font-mono text-xs"
              placeholder="https://clearcommunicationapp.com/..."
              data-testid="urls-textarea"
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">
                {urls.split('\n').map((u) => u.trim()).filter(Boolean).length} URL(s)
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={submitSitemap}
                  disabled={sitemapBusy || busy}
                  className="border-[#8B5CF6]/40 hover:bg-[#8B5CF6]/10 h-8 text-xs"
                  data-testid="submit-sitemap-btn"
                >
                  {sitemapBusy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      <FileText className="h-3.5 w-3.5 mr-1" /> Submit entire sitemap
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={submitPing}
                  disabled={busy || sitemapBusy}
                  className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] h-8 text-xs"
                  data-testid="submit-ping-btn"
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>Submit to API</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {lastResult && (
            <div className="rounded-md border bg-muted/30 p-3" data-testid="last-run-result">
              <p className="text-xs font-medium mb-1.5">
                Last submission · {lastResult.succeeded}/{lastResult.submitted} ok
              </p>
              <ul className="text-[11px] space-y-1 max-h-40 overflow-auto">
                {lastResult.results.slice(0, 20).map((r) => (
                  <li key={r.url} className="flex items-start gap-2">
                    {r.ok ? (
                      <CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <AlertCircle className="h-3 w-3 text-red-500 mt-0.5 shrink-0" />
                    )}
                    <span className="font-mono break-all">{r.url}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {history.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1.5">Recent runs</p>
              <ul className="space-y-1 text-[11px]">
                {history.map((run) => (
                  <li
                    key={run.ran_at}
                    className="flex items-center justify-between gap-2 py-1 border-b last:border-0"
                  >
                    <span className="text-muted-foreground">
                      {new Date(run.ran_at).toLocaleString()}
                    </span>
                    <Badge variant={run.failure_count === 0 ? 'default' : 'secondary'}>
                      {run.success_count}/{run.url_count}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default GscIndexingTab;
